// /admin/background-tasks — live scan queue view (T129).
//
// Three sections rendered in priority order:
//   1. Running   — scans the worker is currently processing.
//   2. Queued    — scans waiting to be picked up by a worker.
//   3. Failed    — scans that ended with an error, with a retry button.
//
// Each row is expandable to show per-task details (commit SHA, project ID,
// timing). Auto-refreshes every 10 seconds via SWR refreshInterval.
//
// Backend: GET /api/v1/admin/background-tasks (org_admin role required).
//          POST /api/v1/admin/background-tasks/{scanID}/retry

import { useState } from 'react';
import useSWR from 'swr';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  RotateCcw,
  Server,
} from 'lucide-react';
import {
  ADMIN_BACKGROUND_TASKS_KEY,
  adminBackgroundTasksApi,
  ApiError,
  BackgroundTask,
  BackgroundTasksResponse,
  fetcher,
} from '../lib/api';
import { PageHeader } from '../components/ui';

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminBackgroundTasks() {
  const { data, error, isLoading, isValidating, mutate } =
    useSWR<BackgroundTasksResponse>(ADMIN_BACKGROUND_TASKS_KEY, fetcher, {
      refreshInterval: 10_000,
      revalidateOnFocus: true,
    });

  const handleRetry = async (scanID: string) => {
    await adminBackgroundTasksApi.retry(scanID);
    mutate();
  };

  const totalActive = data
    ? data.running.length + data.queued.length + data.failed.length
    : 0;

  let taskListContent;
  if (isLoading) {
    taskListContent = <LoadingState />;
  } else if (error) {
    taskListContent = <ErrorBanner error={error} />;
  } else if (data) {
    if (totalActive === 0) {
      taskListContent = <EmptyState />;
    } else {
      taskListContent = (
        <div className="space-y-6">
          <TaskSection
            title="Running"
            description="Scans currently being processed by a worker."
            tasks={data.running}
            statusColor="text-accent"
            StatusIcon={Loader2}
            spinning
            onRetry={handleRetry}
          />
          <TaskSection
            title="Queued"
            description="Scans waiting to be picked up."
            tasks={data.queued}
            statusColor="text-fg-muted"
            StatusIcon={Clock}
            onRetry={handleRetry}
          />
          <TaskSection
            title="Failed"
            description="Scans that ended with an error."
            tasks={data.failed}
            statusColor="text-danger"
            StatusIcon={AlertCircle}
            showRetry
            onRetry={handleRetry}
          />
        </div>
      );
    }
  } else {
    taskListContent = null;
  }

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Activity />}
        title="Background Tasks"
        description={
          <>
            Live view of the scan queue: running, waiting, and failed tasks.
            Auto-refreshes every 10 seconds.
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => mutate()}
            disabled={isValidating}
            aria-label="Refresh background tasks"
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              border border-border text-fg-muted hover:text-fg hover:border-fg/20
              transition-colors disabled:opacity-40 disabled:cursor-not-allowed
            "
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isValidating ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </button>
        }
      />

      {taskListContent}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TaskSection
// ---------------------------------------------------------------------------

interface TaskSectionProps {
  readonly title: string;
  readonly description: string;
  readonly tasks: BackgroundTask[];
  readonly statusColor: string;
  readonly StatusIcon: typeof Server;
  readonly spinning?: boolean;
  readonly showRetry?: boolean;
  readonly onRetry: (id: string) => Promise<void>;
}

function TaskSection({
  title,
  description,
  tasks,
  statusColor,
  StatusIcon,
  spinning = false,
  showRetry = false,
  onRetry,
}: TaskSectionProps) {
  if (tasks.length === 0) return null;

  return (
    <section className="orb-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center gap-3">
        <StatusIcon
          className={`h-4 w-4 ${statusColor} ${spinning ? 'animate-spin' : ''}`}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <h2 className="orb-section-title text-sm">{title}</h2>
          <p className="text-xs text-fg-muted">{description}</p>
        </div>
        <span className="text-xs text-fg-muted tabular-nums">
          {tasks.length} task{tasks.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul>
        {tasks.map((task, i) => (
          <TaskRow
            key={task.id}
            task={task}
            isLast={i === tasks.length - 1}
            showRetry={showRetry}
            onRetry={onRetry}
          />
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TaskRow — expandable row with per-task drilldown
// ---------------------------------------------------------------------------

interface TaskRowProps {
  readonly task: BackgroundTask;
  readonly isLast: boolean;
  readonly showRetry: boolean;
  readonly onRetry: (id: string) => Promise<void>;
}

function TaskRow({ task, isLast, showRetry, onRetry }: TaskRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState<string | null>(null);

  const handleRetry = async () => {
    setRetryErr(null);
    setRetrying(true);
    try {
      await onRetry(task.id);
    } catch (e) {
      setRetryErr(e instanceof ApiError ? e.message : 'Retry failed.');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <li className={`${isLast ? '' : 'border-b border-border'}`}>
      {/* Summary row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          onClick={() => setExpanded((x) => !x)}
          className="p-1 text-fg-muted hover:text-fg transition-colors shrink-0"
          aria-label={expanded ? 'Collapse task details' : 'Expand task details'}
        >
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          }
        </button>

        <div className="flex-1 min-w-0">
          <p className="font-mono text-xs text-fg truncate">{task.id}</p>
          <p className="text-[11px] text-fg-muted truncate mt-0.5">
            project <span className="font-mono">{task.project_id.slice(0, 8)}…</span>
            {task.head_commit_sha && (
              <> · commit <span className="font-mono">{task.head_commit_sha.slice(0, 7)}</span></>
            )}
          </p>
        </div>

        <div className="text-right shrink-0">
          <p className="text-[11px] text-fg-muted tabular-nums">
            {fmtAge(task.created_at)}
          </p>
          {task.started_at && !task.finished_at && (
            <p className="text-[10px] text-fg-subtle">running for {fmtAge(task.started_at)}</p>
          )}
        </div>

        {showRetry && (
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="
              ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs
              font-semibold border border-border text-fg-muted
              hover:text-accent hover:border-accent/40 hover:bg-accent/5
              transition-colors disabled:opacity-30 disabled:cursor-not-allowed
            "
            title="Re-queue this failed scan"
          >
            {retrying
              ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              : <RotateCcw className="h-3 w-3" aria-hidden="true" />
            }
            Retry
          </button>
        )}
      </div>

      {/* Error badge on retry failure */}
      {retryErr && (
        <div className="px-4 pb-2 text-xs text-danger">{retryErr}</div>
      )}

      {/* Drilldown panel */}
      {expanded && <TaskDrilldown task={task} />}
    </li>
  );
}

// ---------------------------------------------------------------------------
// TaskDrilldown
// ---------------------------------------------------------------------------

function TaskDrilldown({ task }: { readonly task: BackgroundTask }) {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Scan ID', value: task.id },
    { label: 'Project ID', value: task.project_id },
    { label: 'Branch ID', value: task.branch_id },
    { label: 'Commit SHA', value: task.head_commit_sha || '—' },
    { label: 'Status', value: task.status },
    { label: 'Created', value: fmtDate(task.created_at) },
  ];
  if (task.started_at) rows.push({ label: 'Started', value: fmtDate(task.started_at) });
  if (task.finished_at) rows.push({ label: 'Finished', value: fmtDate(task.finished_at) });
  if (task.trace_id) rows.push({ label: 'Trace ID', value: task.trace_id });
  if (task.error_message) rows.push({ label: 'Error', value: task.error_message });

  return (
    <div className="border-t border-border bg-bg-inset/30 px-4 py-3">
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5">
        {rows.map(({ label, value }) => (
          <>
            <dt key={`${label}-dt`} className="text-[11px] text-fg-subtle font-medium whitespace-nowrap">
              {label}
            </dt>
            <dd
              key={`${label}-dd`}
              className={`text-[11px] font-mono text-fg break-all ${label === 'Error' ? 'text-danger' : ''}`}
            >
              {value}
            </dd>
          </>
        ))}
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status icon chip used in section headers — maps to appropriate color
// ---------------------------------------------------------------------------

function StatusChip({ status }: { readonly status: string }) {
  switch (status) {
    case 'running':
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-accent">
          <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden="true" />
          running
        </span>
      );
    case 'queued':
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-fg-muted">
          <Clock className="h-2.5 w-2.5" aria-hidden="true" />
          queued
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-danger">
          <AlertCircle className="h-2.5 w-2.5" aria-hidden="true" />
          failed
        </span>
      );
    default:
      return <span className="text-[10px] text-fg-muted">{status}</span>;
  }
}

// Suppress unused warning — StatusChip is exported for potential reuse.
void StatusChip;

// ---------------------------------------------------------------------------
// Empty / loading / error states
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="orb-card p-10 flex flex-col items-center gap-3 text-center">
      <CheckCircle2 className="h-8 w-8 text-success/60" aria-hidden="true" />
      <p className="text-sm font-semibold text-fg">Queue is empty</p>
      <p className="text-xs text-fg-muted max-w-xs">
        No scans are currently running, waiting, or failed. Submit a scan via
        the scanner CLI or CI integration to see activity here.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Loading background tasks…</span>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can view background tasks.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtAge(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  } catch {
    return iso;
  }
}
